import "@mantine/core/styles.css";
import "@mantine/dates/styles.css";
import "@mantine/notifications/styles.css";
import "@mantine/charts/styles.css";
import "@mantine/tiptap/styles.css";
import { Notifications } from "@mantine/notifications";
import { theme } from "./theme";

import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
} from "@remix-run/react";
import { ErrorBoundary } from "./ErrorBoundary";
import "./App.css";
import {
  CSSVariablesResolver,
  ColorSchemeScript,
  MantineColorScheme,
  MantineProvider,
  Container,
  Title,
  Text,
  Button,
  Stack,
  Paper,
  Group,
  Box,
} from "@mantine/core";
import { useState } from "react";
import { json, LoaderFunctionArgs, redirect } from "@remix-run/node";
import { getSession } from "session.server";
import database from "./postgrest/database";
import type { IUser } from "./models/user";
import { RegionProvider } from "~/contexts/RegionContext";
import { loadUserRegions, type UserRegionData } from "~/utils/regionHelpers";
import { ensureSchedulerStarted } from "~/utils/ensureScheduler.server";

// Define the resolver outside the component
const resolver: CSSVariablesResolver = (theme) => ({
  variables: {},
  light: {},
  dark: {},
});

export interface RootLoaderData {
  currentUser: IUser | null;
  selectedApp: string;
  selectedRegions: string[];
  regionData: UserRegionData | null;
}

// Helper function to validate property tokens
async function validatePropertyToken(
  token: string,
  propertyID: string
): Promise<boolean> {
  try {
    const propertyReport = await database.findOne("property_report", {
      propertyToken: token,
      propertyID: propertyID,
    });
    return !!propertyReport;
  } catch (error) {
    console.error("Error validating property token:", error);
    return false;
  }
}

export async function loader({ request }: LoaderFunctionArgs) {
  // Ensure the background scheduler is running. Production startup can't start it reliably,
  // so kick it off (idempotently) on the first request to any page. Non-blocking. (NYMBL-74839)
  ensureSchedulerStarted();

  const session = await getSession(request.headers.get("Cookie"));
  const auth = session.get("auth");
  const username = auth?.username;
  const selectedApp = session.get("selectedApp") || "Schedules";
  const selectedRegions = session.get("selectedRegions") || [];

  const url = new URL(request.url);
  const isLoginRoute = url.pathname === "/auth/login";
  const isGoogleAuthRoute = url.pathname.startsWith("/auth/google");
  const isPublicValetRoute = url.pathname.startsWith("/public/valet");

  // Check for valid token (for public routes and property report)
  const token = url.searchParams.get("token");
  const propertyID = url.searchParams.get("propertyID");

  // For public valet routes, allow access if token and propertyID are present (validation happens in route loader)
  const hasValidToken =
    isPublicValetRoute && token && propertyID
      ? true
      : token && propertyID
      ? await validatePropertyToken(token, propertyID)
      : false;

  // Redirect unauthenticated users (except on login page, mid-OAuth, or with valid token) to login
  if (!username && !isLoginRoute && !isGoogleAuthRoute && !hasValidToken) {
    return redirect("/auth/login");
  }

  // If on login page or user not present, provide null user data
  if (!username) {
    return json<RootLoaderData>({
      currentUser: null,
      selectedApp,
      selectedRegions,
      regionData: null,
    });
  }

  // Load authenticated user. Deactivation blocks new logins (auth.server.ts)
  // but does not invalidate already-issued session cookies, so a deactivated
  // user's session is treated as logged out here — on every page, not only
  // the requireRole-guarded ones. The login/OAuth routes are exempt from the
  // redirect: the login loader destroys the stale session itself, and
  // redirecting there again would loop.
  const currentUser = await database.findOne("user", { username });
  if (!currentUser || currentUser.isDeactivated) {
    if (isLoginRoute || isGoogleAuthRoute) {
      return json<RootLoaderData>({
        currentUser: null,
        selectedApp,
        selectedRegions,
        regionData: null,
      });
    }
    return redirect("/auth/login");
  }

  // Load region data for the authenticated user
  const regionData = await loadUserRegions(currentUser);

  return json<RootLoaderData>({
    currentUser,
    selectedApp,
    selectedRegions,
    regionData,
  });
}

export function Layout({ children }: { children: React.ReactNode }) {
  const [colorScheme] = useState<MantineColorScheme>("auto");

  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link
          rel="preconnect"
          href="https://fonts.gstatic.com"
          crossOrigin=""
        />
        <link
          href="https://fonts.googleapis.com/css2?family=Inter:wght@100;200;300;400;500;600;700;800;900&display=swap"
          rel="stylesheet"
        />
        <Meta />
        <Links />
        <ColorSchemeScript />
      </head>
      <body>
        <MantineProvider
          theme={theme}
          defaultColorScheme={colorScheme}
          cssVariablesResolver={resolver}
        >
          <Notifications autoClose={5000} limit={5} />
          {children}
        </MantineProvider>
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export default function App() {
  const { currentUser, selectedApp, selectedRegions, regionData } =
    useLoaderData<RootLoaderData>();
  return (
    <RegionProvider initialRegions={selectedRegions}>
      <Outlet context={{ currentUser, selectedApp, regionData }} />
    </RegionProvider>
  );
}

// Export ErrorBoundary from separate file
export { ErrorBoundary };
